import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, PasswordInput, Stack, Text, TextInput, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconLock, IconUser } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { DEFAULT_TENANT_SLUG, SHOW_TENANT_FIELD } from '../api/config';
import { login } from '../auth/authApi';
import { useAuthStore } from '../auth/authStore';
import { LanguageSwitcher } from '../components/AppShell/LanguageSwitcher';
import { Logo } from '../components/Logo';

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);
  // ProtectedRoute stashes the URL it bounced from so login returns there.
  const returnTo =
    typeof (location.state as { from?: unknown } | null)?.from === 'string'
      ? ((location.state as { from: string }).from)
      : '/';

  const [tenantSlug, setTenantSlug] = useState(DEFAULT_TENANT_SLUG);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');

  const mutation = useMutation({
    mutationFn: () => login(tenantSlug, { identifier, password }),
    onSuccess: (response) => {
      setSession(response);
      navigate(returnTo, { replace: true });
    },
    onError: () => {
      notifications.show({
        color: 'red',
        title: t('login.errors.invalid'),
        message: '',
      });
    },
  });

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-[1.1fr_1fr]">
      {/* Brand panel — visible at lg+. Subtle radial accents keep it from
          feeling like a flat block of color. */}
      <aside
        className="relative hidden flex-col justify-between overflow-hidden bg-[var(--wheels-sidebar-bg)] p-12 text-white lg:flex"
        aria-hidden="true"
      >
        <div
          className="absolute inset-0 -z-10 opacity-50"
          style={{
            backgroundImage:
              'radial-gradient(circle at 25% 15%, rgba(181,25,29,0.35), transparent 45%), radial-gradient(circle at 80% 75%, rgba(155,20,23,0.25), transparent 50%)',
          }}
        />

        <div>
          <Logo height={32} variant="on-dark" />
        </div>

        <div className="max-w-md">
          <Title order={1} c="white" mb="md" fz={36} lh={1.2} fw={600}>
            {t('login.brand.title')}
          </Title>
          <Text c="rgba(255,255,255,0.72)" fz="md" lh={1.6}>
            {t('login.brand.tagline')}
          </Text>
        </div>

        <Text c="rgba(255,255,255,0.4)" fz="xs">
          {t('login.brand.copyright')}
        </Text>
      </aside>

      {/* Form panel */}
      <main className="flex flex-col bg-white">
        <header className="flex items-center justify-between p-6">
          {/* Small-screen brand mark — hidden when the aside is visible. */}
          <div className="lg:invisible">
            <Logo height={22} />
          </div>
          <LanguageSwitcher />
        </header>

        <div className="flex flex-1 items-center justify-center px-6 pb-10">
          <div className="w-full max-w-md">
            <Title order={2} mb={6} c="var(--wheels-ink-strong)" fw={600}>
              {t('login.title')}
            </Title>
            <Text c="var(--wheels-ink-muted)" mb="xl" fz="sm">
              {t('login.subtitle')}
            </Text>

            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (!tenantSlug.trim() || !identifier.trim() || !password) {
                  notifications.show({
                    color: 'red',
                    title: t('login.errors.missing'),
                    message: '',
                  });
                  return;
                }
                mutation.mutate();
              }}
            >
              <Stack gap="md">
                {/* Tenant slug field — visible whenever the host isn't a
                    tenant subdomain (dev on localhost, staging on the
                    Azure default *.azurewebsites.net). Production with
                    a custom domain like backoffice.example.com hides
                    the field via VITE_SHOW_TENANT_FIELD=false. */}
                {SHOW_TENANT_FIELD && (
                  <TextInput
                    label={t('login.tenantLabel')}
                    description={t('login.tenantHelp')}
                    placeholder={t('login.tenantPlaceholder')}
                    value={tenantSlug}
                    onChange={(event) => setTenantSlug(event.currentTarget.value)}
                    autoComplete="off"
                    required
                  />
                )}
                <TextInput
                  label={t('login.identifierLabel')}
                  placeholder={t('login.identifierPlaceholder')}
                  value={identifier}
                  onChange={(event) => setIdentifier(event.currentTarget.value)}
                  leftSection={<IconUser size={16} stroke={1.6} />}
                  autoComplete="username"
                  required
                />
                <PasswordInput
                  label={t('login.passwordLabel')}
                  value={password}
                  onChange={(event) => setPassword(event.currentTarget.value)}
                  leftSection={<IconLock size={16} stroke={1.6} />}
                  autoComplete="current-password"
                  required
                />
                <Button
                  type="submit"
                  loading={mutation.isPending}
                  fullWidth
                  size="md"
                  rightSection={
                    <IconArrowLeft
                      size={16}
                      stroke={1.8}
                      style={{ transform: isRtl ? 'none' : 'rotate(180deg)' }}
                    />
                  }
                >
                  {t('login.submit')}
                </Button>
              </Stack>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
